import { TFunction } from 'i18next';

import { callGet } from '@/api/fetcher';
import { WHATSAPP_OAUTH_EVENTS } from '@/shared/constants/whatsapp';

import { FACEBOOK_API_VERSION, WHATSAPP_OAUTH_CONFIG_ID } from './constants';

const WA_EMBEDDED_SIGNUP_TYPE = 'WA_EMBEDDED_SIGNUP';

// Meta can post from www., web. or business.facebook.com, so accept any facebook.com subdomain.
const isFacebookOrigin = (origin: string) => {
  try {
    const { protocol, hostname } = new URL(origin);
    return (
      protocol === 'https:' && (hostname === 'facebook.com' || hostname.endsWith('.facebook.com'))
    );
  } catch {
    return false;
  }
};
const SDK_VERSION = FACEBOOK_API_VERSION;

type LoginStatusResponse = { authResponse: { code?: string }; status: string };

export type WhatsappConflict =
  | { kind: 'with-access'; slug: string; integrationId: string; deskId: string }
  | { kind: 'no-access' };

export type OAuthAction =
  | { type: 'SET_SESSION_INFO'; phoneNumberId?: string; wabaId: string; oauthEvent: string }
  | { type: 'SET_OAUTH_CODE'; code: string }
  | { type: 'SET_OAUTH_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_CONNECTING'; isConnecting: boolean }
  | { type: 'SET_CONFLICT'; conflict: WhatsappConflict }
  | { type: 'RESET' };

// Called on the intermediate selection event with the chosen phone_number_id (see below).
type OnSelectNumber = (phoneNumberId: string) => void;

// https://developers.facebook.com/docs/whatsapp/embedded-signup/embed-the-flow#step-7--optional---session-logging
// Exported for unit testing — the real Meta flow can't be driven without a WhatsApp account.
export const createSessionInfoListener =
  (dispatch: (action: OAuthAction) => void, onSelectNumber?: OnSelectNumber) =>
  (event: { origin: string; data: string }) => {
    if (!isFacebookOrigin(event.origin)) {
      return;
    }
    try {
      const data = JSON.parse(event.data);
      if (data.type !== WA_EMBEDDED_SIGNUP_TYPE) {
        return;
      }

      const isFinishEvent =
        data.event === WHATSAPP_OAUTH_EVENTS.FINISH ||
        data.event === WHATSAPP_OAUTH_EVENTS.FINISH_COEXISTENCE ||
        data.event === WHATSAPP_OAUTH_EVENTS.FINISH_ONLY_WABA;

      if (isFinishEvent) {
        // if the user finishes the Embedded Signup flow
        // Note: For coexistence flow, phone_number_id may not be present in the event data
        const { phone_number_id, waba_id } = data.data;
        dispatch({
          type: 'SET_SESSION_INFO',
          phoneNumberId: phone_number_id,
          wabaId: waba_id,
          oauthEvent: data.event,
        });
        return;
      }

      // Intermediate selection event (before FINISH): key off the payload's phone_number_id, not
      // the event name — Meta renames these (SELECTED/BUSINESS_SELECTED/…). Lets us preflight
      // early; we can't close Meta's popup, so /callback preflight stays the backstop.
      const selectedPhoneNumberId = data.data?.phone_number_id;
      if (selectedPhoneNumberId && onSelectNumber) {
        onSelectNumber(selectedPhoneNumberId);
      }
    } catch (error) {
      // Don't parse info that's not JSON
      console.warn('Facebook event did not return JSON data', error);
    }
  };

export type OAuthFeatureType = 'standard' | 'coexistence';

interface LoginOptions {
  featureType?: OAuthFeatureType;
  wabaId?: string;
  phoneNumberId?: string;
}

// Embedded Signup v4: products and permissions come from the configuration (config_id).
// https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation
const initializeFacebookLogin = (options: LoginOptions = {}) => {
  const { featureType = 'standard', wabaId, phoneNumberId } = options;

  return new Promise<LoginStatusResponse>((resolve) => {
    window.FB.getLoginStatus((getStatusResponse) => {
      // If the user has completed the flow before we have to perform a logout
      if (getStatusResponse?.status === 'connected' && window.FB.getAccessToken()) {
        window.FB.logout();
      }

      // Pre-select existing WABA and phone number for reauthorization flows
      const setup: Record<string, unknown> = {};
      if (wabaId) {
        setup.waba_id = wabaId;
      }
      if (phoneNumberId) {
        setup.phone_number_id = phoneNumberId;
      }

      // v4 docs do not mention featureType; keep it so coexistence onboarding still gets requested.
      const extras: Record<string, unknown> = { setup };
      if (featureType === 'coexistence') {
        extras.featureType = 'whatsapp_business_app_onboarding';
      }

      // Login opens the oAuth flow pop-up window
      window.FB.login(
        (loginResponse) => {
          resolve(loginResponse);
        },
        {
          config_id: WHATSAPP_OAUTH_CONFIG_ID,
          response_type: 'code',
          override_default_response_type: true,
          extras,
        }
      );
    });
  });
};

const handleLoginResponse = (
  loginResponse: LoginStatusResponse | undefined,
  dispatch: (action: OAuthAction) => void,
  t: TFunction
) => {
  const code = loginResponse?.authResponse?.code;
  if (code) {
    dispatch({ type: 'SET_OAUTH_CODE', code });
  } else {
    dispatch({
      type: 'SET_OAUTH_ERROR',
      error: t('integrations.facebook.errors.process_interrupted'),
    });
  }
};

export const performWhatsappOAuth = async (
  dispatch: (action: OAuthAction) => void,
  t: TFunction,
  featureType: OAuthFeatureType = 'standard',
  wabaId?: string,
  phoneNumberId?: string,
  onSelectNumber?: OnSelectNumber
) => {
  const sessionInfoListener = createSessionInfoListener(dispatch, onSelectNumber);

  window.addEventListener('message', sessionInfoListener);

  try {
    const response = await initializeFacebookLogin({ featureType, wabaId, phoneNumberId });

    handleLoginResponse(response, dispatch, t);
  } catch (error) {
    // Error during WhatsApp oAuth
    console.warn('Error when performing WhatsApp oAuth', error);
    dispatch({
      type: 'SET_OAUTH_ERROR',
      error: error.message || t('integrations.facebook.errors.no_code', { 0: 'WhatsApp' }),
    });
  } finally {
    // Remove the event listener when the OAuth process is complete
    window.removeEventListener('message', sessionInfoListener);
  }
};

// https://developers.facebook.com/docs/whatsapp/embedded-signup/embed-the-flow#step-1--load-the-facebook-javascript-sdk
export const initializeFacebookSdk = async () => {
  const appId = await callGet('/www/api/integrations/facebook/app-id');

  window.fbAsyncInit = () => {
    window.FB.init({ appId: appId, cookie: true, xfbml: true, version: SDK_VERSION });
  };

  const scriptId = 'facebook-jssdk';
  const existingScript = document.getElementById(scriptId);

  if (!existingScript) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);
  }
};
